import type { Command } from '@blocksuite/std';
import type { BlockModel, Store } from '@blocksuite/store';

import { DEFAULT_COLUMN_WIDTH } from '../styles.js';

export const MAX_COLUMNS = 10;
export const MAX_ROWS = 50;

/** Builds `rowCount` rows of `columnCount` cells, one paragraph per cell. */
export function buildRichTableRows(
  store: Store,
  tableId: string,
  columnCount: number,
  rowCount: number,
  index?: number
) {
  for (let r = 0; r < rowCount; r++) {
    const rowId = store.addBlock(
      'affine:rich-table-row',
      {},
      tableId,
      index === undefined ? undefined : index + r
    );
    for (let c = 0; c < columnCount; c++) {
      const cellId = store.addBlock('affine:rich-table-cell', {}, rowId);
      store.addBlock('affine:paragraph', {}, cellId);
    }
  }
}

/**
 * Inserts an `affine:rich-table` next to the selection, every cell seeded with
 * an empty paragraph so there is somewhere to type immediately.
 */
export const insertRichTableBlockCommand: Command<
  {
    selectedModels?: BlockModel[];
    columnCount: number;
    rowCount: number;
    headerRow?: boolean;
    place?: 'after' | 'before';
    removeEmptyLine?: boolean;
  },
  { insertedRichTableBlockId: string }
> = (ctx, next) => {
  const {
    selectedModels,
    columnCount,
    rowCount,
    headerRow = true,
    place,
    removeEmptyLine,
    std,
  } = ctx;
  if (!selectedModels?.length) return;
  if (columnCount < 1 || rowCount < 1) return;
  if (columnCount > MAX_COLUMNS || rowCount > MAX_ROWS) return;

  const targetModel =
    place === 'before'
      ? selectedModels[0]
      : selectedModels[selectedModels.length - 1];
  if (!targetModel) return;

  const tableId = std.store.addSiblingBlocks(
    targetModel,
    [
      {
        flavour: 'affine:rich-table',
        columnWidths: Array.from(
          { length: columnCount },
          () => DEFAULT_COLUMN_WIDTH
        ),
        headerRow,
      },
    ],
    place
  )[0];
  if (!tableId) return;

  buildRichTableRows(std.store, tableId, columnCount, rowCount);

  if (removeEmptyLine && targetModel.text?.length === 0) {
    std.store.deleteBlock(targetModel);
  }

  next({ insertedRichTableBlockId: tableId });
};
